import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Check, Clock } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, IMAGES, BTN_GOLD, BTN_OUTLINE_LIGHT } from "@/site";
import { useQuote } from "@/quote-context";

const EASE = [0.22, 1, 0.36, 1];

const TRUST_POINTS = [
    "Available 24/7",
    "Residential • Commercial • Automotive",
    "Professional Service",
    "Fast Local Response",
    "Upfront Pricing",
];

const MaskedLine = ({ children, delay }) => (
    <span className="-mb-1 block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const { openQuote } = useQuote();
    const imgWrapRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

    return (
        <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-navy-950 pt-[72px]">
            <div className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-gold/10 blur-3xl" />
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                }}
            />

            <div className="relative mx-auto grid w-full max-w-7xl gap-14 px-5 pb-24 pt-14 sm:px-8 lg:grid-cols-12 lg:items-center lg:pb-32 lg:pt-24">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-gold"
                    >
                        <span className="h-px w-8 bg-gold" />
                        Family-Owned • Regina, Saskatchewan
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="mt-6 font-display text-[2.6rem] font-semibold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[4.4rem]"
                    >
                        <MaskedLine delay={0.15}>24/7 Locksmith</MaskedLine>
                        <MaskedLine delay={0.28}>
                            Services <span className="italic text-gold">You Can</span>
                        </MaskedLine>
                        <MaskedLine delay={0.41}>
                            <span className="italic text-gold">Trust.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        data-testid="hero-subheadline"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    >
                        Fast, professional locksmith services for your home, business, and vehicle. Whether you're
                        locked out, need a lock repaired, or need a new car key, we're ready to help — day or night.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <a data-testid="hero-call-now-button" href={PHONE_TEL} className={BTN_GOLD}>
                            <Phone className="h-4 w-4" />
                            Call Now — Available 24/7
                        </a>
                        <button data-testid="hero-quote-button" onClick={() => openQuote()} className={BTN_OUTLINE_LIGHT}>
                            Get a Free Quote
                        </button>
                    </motion.div>

                    <motion.ul
                        data-testid="hero-trust-points"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.95 }}
                        className="mt-9 flex flex-wrap gap-x-6 gap-y-2.5"
                    >
                        {TRUST_POINTS.map((p) => (
                            <li key={p} className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                                <Check className="h-4 w-4 shrink-0 text-gold" />
                                {p}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <div className="lg:col-span-5" ref={imgWrapRef}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                        className="relative"
                    >
                        <div className="overflow-hidden rounded-[1.75rem] border border-white/10 shadow-2xl shadow-black/40">
                            <motion.img
                                style={{ y, scale: 1.18 }}
                                src={IMAGES.hero}
                                alt="Professional locksmith servicing a residential door lock"
                                className="h-[400px] w-full object-cover sm:h-[500px]"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
                            className="absolute -bottom-6 left-4 rounded-2xl border border-white/10 bg-navy-900/90 p-5 shadow-xl shadow-black/40 backdrop-blur-md sm:-left-8"
                        >
                            <div className="flex items-center gap-3">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-gold">
                                    <Clock className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                        Emergency Line — 24/7
                                    </p>
                                    <a
                                        data-testid="hero-badge-phone"
                                        href={PHONE_TEL}
                                        className="font-display text-xl font-semibold text-white transition-colors hover:text-gold"
                                    >
                                        {PHONE_DISPLAY}
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
